/* eslint camelcase: 0 */
// @ts-check

import isEqual from 'lodash/isEqual';
import {AttrsSchema, Component, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {SankeyResponse} from 'common/report/funnels/models/api-types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {SankeyFlow} from 'common/widgets/sankey-chart/models/sankey-flow';
import {getProjectId} from 'common/report/util';
import {SankeyChartEl} from 'common/widgets/sankey-chart';
import {NoDataHero} from 'common/widgets/warning-hero/presets';
import {WarningHeroReportName} from 'common/widgets/warning-hero/constants';
import {EmptyDataEl} from 'common/charts/misc/empty-data';

import css from './index.inline.styl';

interface ChartState {
  funnelQuery: FunnelsQuery;
}

interface ChartAttrs {
  'has-hero-empty-state'?: boolean;
  'disable-click-node'?: boolean;
}

interface FunnelsTopPathsProps {
  funnelsResponse: SankeyResponse;
  bookmark: FunnelBookmark;
}

export interface FunnelsTopPathsJsxProps {
  attrs?: ChartAttrs;
  props: FunnelsTopPathsProps;
}

export class FunnelsTopPathsChart extends Component<ChartState, ChartAttrs> {
  private _funnelsResponse: SankeyResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      ...super.attrsSchema,
      'has-hero-empty-state': `boolean`,
      'disable-click-node': `boolean`,
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.updateFunnelsQuery();
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      defaultState: {
        funnelQuery: null,
      },
      template: () => this.renderTemplate(),
      helpers: {
        getSankeyChartAttrs: () => {
          return {
            alignbreakdowns: true,
            alwaysbreakdown: false,
            reverse: false,
            'tooltip-enabled': true,
            'disable-add-step': true,
            'disable-anchor-event-labels': true,
            'disable-click-node': this.attr(`disable-click-node`),
            'event-rename': this.state.funnelQuery.getStepLabels(true),
          };
        },

        getSankeyChartProps: () => {
          const {data} = this.funnelsResponse;
          const dateKey = Object.keys(data)[0];
          const sankeyData = data[dateKey];
          return {
            // @ts-ignore SankeyStep vs SerializedSankeyStep ??
            data: new SankeyFlow(sankeyData, this.state.funnelQuery.events),
          };
        },
      },
    };
  }

  private renderTemplate() {
    if (!this.state.funnelQuery) {
      return <div sel='.root' />;
    }

    return (
      <div sel='.root'>
        <SankeyChartEl attrs={this.helpers.getSankeyChartAttrs()} props={this.helpers.getSankeyChartProps()}>
          <div attrs={{slot: `empty-state`}}>{this.renderEmptyState()}</div>
        </SankeyChartEl>
      </div>
    );
  }

  private renderEmptyState() {
    return this.attr(`has-hero-empty-state`) ? (
      <NoDataHero
        reportName={WarningHeroReportName.Funnels}
        bodyText={`We couldn't find any data for that date range.`}
      />
    ) : (
      <EmptyDataEl />
    );
  }

  public set funnelsResponse(response: SankeyResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.updateFunnelsQuery();
    }
  }

  public get funnelsResponse(): SankeyResponse {
    return this._funnelsResponse;
  }

  public set bookmark(bookmark: FunnelBookmark) {
    if (!isEqual(this._bookmark, bookmark)) {
      this._bookmark = bookmark;
      this.updateFunnelsQuery();
    }
  }

  public get bookmark(): FunnelBookmark {
    return this._bookmark;
  }

  updateFunnelsQuery() {
    if (!this._bookmark || !this.isConnected) {
      return;
    }

    // Note: normally the bookmark is corrected via getBookmarkWithAppliedDefaults() which uses FunnelsQuery to apply corrections
    // but this widget already directly uses the underlying FunnelsQuery class.
    const funnelQuery = FunnelsQuery.fromBookmark(this.bookmark, parseInt(getProjectId(), 10));

    this.update({funnelQuery});
  }
}

export const FunnelsTopPathsChartEl = defineAndMakeJsxFactory<FunnelsTopPathsJsxProps>(
  `mp-funnels-top-paths-chart`,
  FunnelsTopPathsChart,
);
export const FunnelsTopPathsChartReact = panel2react<FunnelsTopPathsJsxProps>(
  `mp-funnels-top-paths-chart`,
  FunnelsTopPathsChart,
);
